import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NIGERIAN_STATES } from "@/lib/constants";
import { Download, DatabaseBackup, Loader2, Filter, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = href; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(href);
}

export function AdminDataTools({ isSuper }) {
  const [open, setOpen] = useState(false);
  const [programmes, setProgrammes] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [f, setF] = useState({ programme: "", sponsor: "", state: "", lga: "", ward: "", status: "", date_from: "", date_to: "", format: "xlsx" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v === ALL ? "" : v }));
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const runPreview = async () => {
    setPreviewBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v && k !== "format") params.append(k, v); });
      const { data } = await api.get(`/admin/beneficiaries/export-preview?${params.toString()}`);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPreviewBusy(false); }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/admin/programmes").then((r) => setProgrammes(r.data.programmes)).catch(() => {});
    api.get("/admin/sponsors").then((r) => setSponsors(r.data.sponsors)).catch(() => {});
    runPreview();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const runExport = async () => {
    setExportBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      await downloadBlob(`/admin/beneficiaries/export?${params.toString()}`, `tmf-beneficiaries.${f.format === "csv" ? "csv" : "xlsx"}`);
      toast.success("Beneficiary export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExportBusy(false); }
  };

  const runBackup = async () => {
    setBackupBusy(true);
    try {
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      await downloadBlob("/admin/backup", `tmf-backup-${ts}.gz`);
      toast.success("Database backup downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackupBusy(false); }
  };

  const runReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/reset-operational-data", { confirm: resetText });
      toast.success(`Cleared ${data.total} records. The site is now clean.`);
      setResetOpen(false); setResetText("");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetBusy(false); }
  };

  return (
    <>
      <Button data-testid="beneficiary-export-btn" variant="outline" className="border-emerald-200 text-emerald-800" onClick={() => setOpen(true)}>
        <Download className="w-4 h-4 mr-2" /> Export Beneficiaries
      </Button>
      {isSuper && (
        <Button data-testid="db-backup-btn" variant="outline" className="border-indigo-200 text-indigo-800" onClick={runBackup} disabled={backupBusy}>
          {backupBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-2" />} Backup DB
        </Button>
      )}
      {isSuper && (
        <Button data-testid="reset-data-btn" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => { setResetText(""); setResetOpen(true); }}>
          <Trash2 className="w-4 h-4 mr-2" /> Reset Data
        </Button>
      )}

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setResetText(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Reset Operational Data</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <p>This permanently deletes <b>all</b> sponsors, vendors, beneficiaries, programmes, vouchers, transactions, payments, settlements, complaints, SMS logs and sponsor/vendor logins.</p>
            <p>Your <b>admin account</b> and <b>SMS templates</b> are kept. This cannot be undone — download a backup first if unsure.</p>
            <div>
              <Label className="text-red-700">Type <span className="font-mono font-bold">RESET</span> to confirm</Label>
              <Input data-testid="reset-confirm-input" value={resetText} onChange={(e) => setResetText(e.target.value)} className="mt-1.5 h-11" placeholder="RESET" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetOpen(false); setResetText(""); }}>Cancel</Button>
            <Button data-testid="reset-confirm-btn" onClick={runReset} disabled={resetBusy || resetText !== "RESET"} className="bg-red-600 hover:bg-red-700">
              {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Delete Everything</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Filter className="w-4 h-4 text-emerald-600" /> Export Beneficiaries</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Programme</Label>
              <Select value={f.programme || ALL} onValueChange={(v) => set("programme", v)}>
                <SelectTrigger data-testid="export-programme" className="mt-1.5 h-10"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value={ALL}>All programmes</SelectItem>{programmes.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sponsor</Label>
              <Select value={f.sponsor || ALL} onValueChange={(v) => set("sponsor", v)}>
                <SelectTrigger data-testid="export-sponsor" className="mt-1.5 h-10"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value={ALL}>All sponsors</SelectItem>{sponsors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>State</Label>
              <Select value={f.state || ALL} onValueChange={(v) => set("state", v)}>
                <SelectTrigger data-testid="export-state" className="mt-1.5 h-10"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value={ALL}>All states</SelectItem>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Voucher Status</Label>
              <Select value={f.status || ALL} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="export-status" className="mt-1.5 h-10"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{["pending", "active", "fully_redeemed", "expired"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>LGA</Label><Input data-testid="export-lga" value={f.lga} onChange={(e) => set("lga", e.target.value)} className="mt-1.5 h-10" placeholder="optional" /></div>
            <div><Label>Ward</Label><Input data-testid="export-ward" value={f.ward} onChange={(e) => set("ward", e.target.value)} className="mt-1.5 h-10" placeholder="optional" /></div>
            <div><Label>From Date</Label><Input data-testid="export-from" type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="mt-1.5 h-10" /></div>
            <div><Label>To Date</Label><Input data-testid="export-to" type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="mt-1.5 h-10" /></div>
            <div>
              <Label>Format</Label>
              <Select value={f.format} onValueChange={(v) => set("format", v)}>
                <SelectTrigger data-testid="export-format" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="xlsx">Excel (.xlsx)</SelectItem><SelectItem value="csv">CSV (.csv)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 mt-1" data-testid="export-preview-panel">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-emerald-900">
                {previewBusy ? "Counting…" : `${preview?.count ?? 0} beneficiaries match — ready to export`}
              </span>
              <Button data-testid="export-preview-btn" size="sm" variant="outline" onClick={runPreview} disabled={previewBusy} className="h-8 border-emerald-200 text-emerald-800">
                {previewBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Filter className="w-3.5 h-3.5 mr-1" /> Preview</>}
              </Button>
            </div>
            {preview?.rows?.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-emerald-100 bg-white">
                <table className="w-full text-xs" data-testid="export-preview-table">
                  <thead className="sticky top-0 bg-emerald-50"><tr className="text-left text-gray-500">
                    <th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">TMF ID</th><th className="px-2 py-1.5">Programme</th><th className="px-2 py-1.5">State</th><th className="px-2 py-1.5">Status</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={r[1] || `row-${i}`} className="border-t border-emerald-50">
                        <td className="px-2 py-1.5">{r[0] || "—"}</td><td className="px-2 py-1.5 font-mono text-emerald-700">{r[1]}</td><td className="px-2 py-1.5">{r[5] || "—"}</td><td className="px-2 py-1.5">{r[7] || "—"}</td><td className="px-2 py-1.5">{r[14] || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.count > preview.rows.length && <div className="px-2 py-1.5 text-[11px] text-gray-400">Showing first {preview.rows.length} of {preview.count}. Download for the full list.</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="export-run" onClick={runExport} disabled={exportBusy} className="bg-emerald-600 hover:bg-emerald-700">
              {exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Download</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
